import moment from 'moment';
import { FORM_DEFINITIONS } from './form-definitions';
import {
    analyticsToday,
    comparePeriodFor,
    resolvePeriodRequest,
} from './periods';
import type {
    KpiForm,
    Period,
    PeriodPreset,
    Region,
    ResolvedPeriod,
    ResolvedRegion,
} from './types';

/** `moment` formats period bounds as ISO calendar dates. */
const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Resolve a (possibly absent) period into concrete `{start,end}` bounds.
 *
 *   - `undefined`          -> the page filter range (default path)
 *   - custom `{start,end}` -> passthrough
 *   - preset (e.g. '7d')   -> the dates the API will resolve it to
 */
export const resolvePeriod = (
    period: Period | undefined,
    pageRange: ResolvedPeriod
): ResolvedPeriod => {
    if (period === undefined) {
        return pageRange;
    }

    if (typeof period === 'object') {
        return period;
    }

    // Only `campaign` can fail to resolve, and a card cannot pin it.
    return resolvePeriodRequest({ period }, analyticsToday())!;
};

/**
 * Today's UTC calendar date as an ISO `YYYY-MM-DD` string.
 *
 * UTC, unlike the period presets above (Toronto, like the API), because both callers compare it against
 * dates the API produced, and every date in a KPI envelope is a UTC calendar
 * date: `range`, `dataCompleteThrough`, every series point. Read in the
 * browser's zone it would cut the downloads axis a day short for a reader in
 * UTC-5 in the evening, and a day long for a reader east of UTC, on data that
 * never moved.
 */
export const todayISO = (): string => moment.utc().format(DATE_FORMAT);

/**
 * Resolve a (possibly absent) region into a concrete province list.
 *
 *   - `undefined`  -> the page region (default path)
 *   - `string[]`   -> passthrough
 */
export const resolveRegion = (
    region: Region | undefined,
    pageRegion: ResolvedRegion
): ResolvedRegion => {
    return region === undefined ? pageRegion : region;
};

/**
 * Period preset -> human label.
 *
 * Used by the card edit menu, which is the only place a preset is ever named:
 * the (i) popover renders the RESOLVED bounds, which are dates, not a preset.
 */
export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
    '7d': 'Last 7 days',
    '30d': 'Last 30 days',
    '90d': 'Last 90 days',
    'last-week': 'Last week',
    'last-month': 'Last month',
};

/** The preceding window a period is compared with, as the API derives it. */
export const resolveComparePeriod = (
    period: Period
): { range: ResolvedPeriod; label: string } =>
    comparePeriodFor(resolvePeriod(period, { start: '', end: '' }));

/**
 * The RPP dashboard's "Compare to previous period" toggle state, read from its
 * URL param (parsed as a boolean by `useSearchParams`). Absent = ON (the
 * default); only an explicit `false` (`?compare=false`) turns it off.
 */
export const resolveCompareEnabled = (param: boolean | undefined): boolean =>
    param !== false;

/** Whether a card gets a comparison range, and what it is. Which forms compare
 * at all is stated in `FORM_DEFINITIONS`; on top of that the page toggle turns
 * every card's compare off, and a card opts itself out with `trend === false`. */
export const resolveCardCompare = (
    form: KpiForm,
    period: Period,
    trend: boolean | undefined,
    compareEnabled: boolean
): { range: ResolvedPeriod; label: string } | undefined => {
    if (!compareEnabled) return undefined;
    if (!FORM_DEFINITIONS[form].comparable) return undefined;
    if (trend === false) return undefined;
    return resolveComparePeriod(period);
};
